/**
 * Reading, shrinking, and re-encoding an uploaded image in the browser, before
 * it is stored.
 *
 * Two things used to be the editor's problem and are not any more. The first is
 * `width` and `height`: the file already knows them, and a human retyping them
 * can only ever be right by accident. The second is size. A 6 MB hero is not a
 * storage cost, it is a performance bug on a public page, and the only moment
 * anyone can cheaply fix it is before it is uploaded.
 *
 * The third is renditions, and it is the one with a sharp edge. `MediaRef`
 * carries a list of the encodings that exist, and the renderer trusts that list
 * absolutely: a `srcset` candidate or an AVIF `<source>` naming an object that
 * was never uploaded does not degrade gracefully, because a browser that has
 * committed to a candidate does not fall back. It is a broken image where a
 * working one used to be. So nothing here claims a rendition it did not
 * produce, and `canvas.toBlob` is not taken at its word: asked for a format it
 * cannot encode it quietly returns a PNG, so every blob's `type` is checked
 * against what was requested before it counts as a rendition.
 *
 * Everything here needs a DOM, which is why it is separate from `text.ts`. The
 * naming and planning functions are deliberately pure, and are tested.
 */

import type { ImageRendition } from "@zennopay/schema";

export interface ImageDimensions {
    width: number;
    height: number;
}

/** Formats worth producing as extra renditions, best first. */
export const RENDITION_FORMATS = ["avif", "webp"] as const;
export type RenditionFormat = (typeof RENDITION_FORMATS)[number];

export type ImageFormat = ImageRendition["format"];

export interface PreparedImage {
    /** What should actually be uploaded. The original when nothing was gained. */
    file: File;
    /** Dimensions of `file`, i.e. what belongs in `MediaRef`. */
    width: number;
    height: number;
    /** The encoding of `file`, which the rendition plan must not duplicate. */
    format: ImageFormat | undefined;
    original: ImageDimensions & { bytes: number };
    /** True when `file` is not the file the editor chose. */
    resized: boolean;
}

export interface PrepareImageOptions {
    maxWidth?: number;
    maxHeight?: number;
    /** Re-encode above this even when the dimensions are already fine. */
    maxBytes?: number;
    quality?: number;
}

/* A hero renders at most full-bleed on a wide screen; 2x that covers retina.
   Anything larger is downloaded and thrown away by the browser. */
const DEFAULTS: Required<PrepareImageOptions> = {
    maxWidth: 1600,
    maxHeight: 1600,
    maxBytes: 300_000,
    quality: 0.82
};

/**
 * Formats a canvas cannot re-encode without destroying them: an animated GIF
 * loses its animation and an SVG is not a raster image at all.
 */
const NOT_RESIZABLE = ["image/gif", "image/svg+xml"];

/** Natural dimensions of an image file, or undefined if it cannot be decoded. */
export async function measureImage(file: File): Promise<ImageDimensions | undefined> {
    const url = URL.createObjectURL(file);
    try {
        const image = await loadImage(url);
        if (!image.naturalWidth || !image.naturalHeight) return undefined;
        return { width: image.naturalWidth, height: image.naturalHeight };
    } catch {
        return undefined;
    } finally {
        URL.revokeObjectURL(url);
    }
}

function loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("The file could not be decoded as an image."));
        image.src = url;
    });
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
    return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

const withExtension = (name: string, extension: string): string =>
    `${name.replace(/\.[^.]+$/, "")}.${extension}`;

/**
 * The file to upload, its dimensions, and what it was before.
 *
 * Falls back to the original on every failure path rather than refusing the
 * upload: an image that could not be measured is still an image the editor
 * wants, and `MediaRef.width`/`height` are optional in the content model.
 */
export async function prepareImage(
    file: File,
    options: PrepareImageOptions = {}
): Promise<PreparedImage> {
    const { maxWidth, maxHeight, maxBytes, quality } = { ...DEFAULTS, ...options };

    const measured = await measureImage(file);
    const original = {
        width: measured?.width ?? 0,
        height: measured?.height ?? 0,
        bytes: file.size
    };
    const unchanged: PreparedImage = {
        file,
        width: original.width,
        height: original.height,
        format: formatOfMime(file.type),
        original,
        resized: false
    };

    if (!measured || NOT_RESIZABLE.includes(file.type)) return unchanged;

    const scale = Math.min(1, maxWidth / measured.width, maxHeight / measured.height);
    const oversized = scale < 1;
    const heavy = file.size > maxBytes;
    if (!oversized && !heavy) return unchanged;

    const width = Math.max(1, Math.round(measured.width * scale));
    const height = Math.max(1, Math.round(measured.height * scale));

    const url = URL.createObjectURL(file);
    try {
        const image = await loadImage(url);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const canvasContext = canvas.getContext("2d");
        if (!canvasContext) return unchanged;
        canvasContext.drawImage(image, 0, 0, width, height);

        /* WebP for everything, including PNG: it keeps alpha and is roughly a
           third of the bytes. The stored value is a path, so nothing downstream
           cares what the extension was. */
        const blob = await toBlob(canvas, "image/webp", quality);
        if (!blob) return unchanged;

        /* Re-encoding a small, already-optimised image can make it bigger. When
           it does, the original wins, but the measured dimensions are still
           worth keeping. */
        if (!oversized && blob.size >= file.size) return unchanged;

        /* `toBlob` answers an unsupported type with a PNG rather than with
           nothing, so the requested format is a request and not a result. The
           extension and the recorded format follow the bytes that came back;
           a `.webp` that is really a PNG would be a lie told to the CDN. */
        const format = formatOfMime(blob.type) ?? "png";

        return {
            file: new File([blob], withExtension(file.name, extensionFor(format)), {
                type: blob.type
            }),
            width,
            height,
            format,
            original,
            resized: true
        };
    } catch {
        return unchanged;
    } finally {
        URL.revokeObjectURL(url);
    }
}

export function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* ------------------------------------------------------ formats and names -- */

const MIME_BY_FORMAT: Record<ImageFormat, string> = {
    avif: "image/avif",
    webp: "image/webp",
    jpeg: "image/jpeg",
    png: "image/png"
};

const FORMAT_BY_MIME: Record<string, ImageFormat> = {
    "image/avif": "avif",
    "image/webp": "webp",
    "image/jpeg": "jpeg",
    "image/jpg": "jpeg",
    "image/png": "png"
};

export const mimeOf = (format: ImageFormat): string => MIME_BY_FORMAT[format];

export const formatOfMime = (mime: string | undefined): ImageFormat | undefined =>
    FORMAT_BY_MIME[(mime ?? "").toLowerCase()];

/** `.jpg` rather than `.jpeg`, because that is what the rest of the web writes. */
export const extensionFor = (format: ImageFormat): string =>
    format === "jpeg" ? "jpg" : format;

/**
 * A safe, stable stem for an object name.
 *
 * An uploaded file's name comes from somebody's desktop and can carry spaces,
 * accents, `#`, or a hundred characters of them. The object path ends up inside
 * a URL that nginx proxies, so it is normalized once, here, rather than
 * defended against everywhere downstream.
 */
export function fileStem(name: string): string {
    const base = name.replace(/\.[^.]+$/, "");
    const cleaned = base
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    return (cleaned || "image").slice(0, 60).replace(/-+$/g, "") || "image";
}

/**
 * The object name a new upload gets.
 *
 * The token is what makes it unique, and uniqueness is not tidiness here. Two
 * editors uploading `hero.png` used to overwrite each other's object, which
 * silently swapped one guide's image for another's — and with renditions in the
 * picture it would also leave a `MediaRef` listing sizes of an image it is no
 * longer describing. `functions/src/publicMedia.ts` already documents this
 * property as one it relies on for its year-long immutable cache headers.
 */
export function uploadFileName(originalName: string, extension: string, token: string): string {
    const cleaned = extension.replace(/^\.+/, "").toLowerCase().replace(/[^a-z0-9]/g, "");
    return `${fileStem(originalName)}-${token}${cleaned ? `.${cleaned}` : ""}`;
}

/** The extension a prepared file should be stored under, format or not. */
export function extensionOf(file: File, format: ImageFormat | undefined): string {
    if (format) return extensionFor(format);
    const fromName = /\.([a-z0-9]+)$/i.exec(file.name)?.[1];
    return (fromName ?? "").toLowerCase();
}

/** A rendition sits beside its canonical object, named for what it is. */
export function renditionFileName(
    canonicalFileName: string,
    width: number,
    format: ImageFormat
): string {
    return `${fileStem(canonicalFileName)}-${width}w.${extensionFor(format)}`;
}

/** Six characters of randomness plus the clock: unique enough for an object name. */
export function newUploadToken(): string {
    return (
        Math.random().toString(36).slice(2, 8) + Date.now().toString(36).slice(-4)
    );
}

/* -------------------------------------------------------- format support -- */

const encodingSupport = new Map<string, Promise<boolean>>();

async function probeEncoding(mime: string): Promise<boolean> {
    try {
        const canvas = document.createElement("canvas");
        /* Not 1×1: some encoders reject a degenerate image and would be reported
           as unsupported when they are merely fussy. 16×16 costs nothing. */
        canvas.width = 16;
        canvas.height = 16;
        const context = canvas.getContext("2d");
        if (!context) return false;
        context.fillStyle = "#888";
        context.fillRect(0, 0, 16, 16);
        const blob = await toBlob(canvas, mime, 0.8);
        /* The whole point. `toBlob` falls back to PNG for a format it cannot
           encode, so a non-null blob proves nothing and the type is the answer. */
        return blob !== null && blob.type === mime;
    } catch {
        return false;
    }
}

/** Whether this browser can actually encode `mime`. Probed once, then cached. */
export function supportsEncoding(mime: string): Promise<boolean> {
    let cached = encodingSupport.get(mime);
    if (!cached) {
        cached = probeEncoding(mime);
        encodingSupport.set(mime, cached);
    }
    return cached;
}

/** The subset of `candidates` this browser can encode, in the order given. */
export async function encodableFormats(
    candidates: readonly RenditionFormat[] = RENDITION_FORMATS
): Promise<RenditionFormat[]> {
    const supported = await Promise.all(
        candidates.map((format) => supportsEncoding(mimeOf(format)))
    );
    return candidates.filter((_, index) => supported[index] === true);
}

/* ------------------------------------------------------------- the plan -- */

/**
 * Widths worth having on a page whose hero is at most 1600px.
 *
 * Three candidates, not six. Each one is a browser upload, an object in two
 * buckets, and a copy on every publish, and the difference between a 640px and
 * a 720px candidate is invisible to a reader.
 */
export const RENDITION_WIDTHS = [640, 1280];

export interface RenditionSpec {
    format: RenditionFormat;
    width: number;
    height: number;
}

/**
 * What to encode, given what the canonical upload already is.
 *
 * The canonical width is always included, so a browser that prefers AVIF gets a
 * full-size AVIF rather than being pushed back to the fallback at the one size
 * that matters most. The canonical's own format at its own width is skipped:
 * that object already exists, and uploading it twice would put two names on one
 * encoding.
 *
 * Pure, and tested: this is the function that decides what the renderer will be
 * told exists.
 */
export function renditionPlan(
    canonical: { width: number; height: number; format?: ImageFormat | undefined },
    formats: readonly RenditionFormat[],
    widths: readonly number[] = RENDITION_WIDTHS
): RenditionSpec[] {
    if (!canonical.width || !canonical.height || formats.length === 0) return [];

    const candidates = [...new Set([
        ...widths.filter((width) => width > 0 && width < canonical.width),
        canonical.width
    ])].sort((a, b) => a - b);

    const specs: RenditionSpec[] = [];
    for (const format of formats) {
        for (const width of candidates) {
            if (format === canonical.format && width === canonical.width) continue;
            specs.push({
                format,
                width,
                height: Math.max(
                    1,
                    Math.round((canonical.height * width) / canonical.width)
                )
            });
        }
    }
    return specs;
}

/* ---------------------------------------------------------- the encoding -- */

export interface EncodedRendition extends RenditionSpec {
    blob: Blob;
}

/**
 * Encodes each spec from the original file, dropping anything that came back
 * in the wrong format.
 *
 * Encoded from the file the editor chose rather than from the downscaled
 * canonical, because re-encoding an already-lossy WebP compounds its artefacts
 * and the original is decoded here anyway.
 *
 * A spec that fails is dropped silently *from the list*, which is the safe
 * direction: an image with fewer renditions than intended renders perfectly
 * from its canonical object, and an image with one rendition too many does not
 * render at all.
 */
export async function buildRenditions(
    source: File,
    specs: RenditionSpec[],
    quality = DEFAULTS.quality
): Promise<EncodedRendition[]> {
    if (specs.length === 0 || NOT_RESIZABLE.includes(source.type)) return [];

    const url = URL.createObjectURL(source);
    try {
        const image = await loadImage(url);
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        if (!context) return [];

        const encoded: EncodedRendition[] = [];
        for (const spec of specs) {
            canvas.width = spec.width;
            canvas.height = spec.height;
            context.clearRect(0, 0, spec.width, spec.height);
            context.drawImage(image, 0, 0, spec.width, spec.height);

            const mime = mimeOf(spec.format);
            const blob = await toBlob(canvas, mime, quality);
            if (!blob || blob.type !== mime) continue;
            encoded.push({ ...spec, blob });
        }
        return encoded;
    } catch {
        return [];
    } finally {
        URL.revokeObjectURL(url);
    }
}

/** A short sentence for the editor: what was produced, and at what sizes. */
export function describeRenditions(renditions: ImageRendition[]): string {
    if (renditions.length === 0) return "no extra encodings";
    const byFormat = new Map<string, number[]>();
    for (const rendition of renditions) {
        byFormat.set(rendition.format, [
            ...(byFormat.get(rendition.format) ?? []),
            rendition.width
        ]);
    }
    return [...byFormat.entries()]
        .map(([format, widths]) =>
            `${format.toUpperCase()} at ${[...widths].sort((a, b) => a - b).join(", ")}px`)
        .join(" · ");
}
